import {
  faExclamationTriangle,
  faInfoCircle,
  faSave,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import SubtleBadge from 'components/common/SubtleBadge';
import Loading from 'components/loading/Loading';
import Pagination from 'components/pagination';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import useApi from 'helpers/api';
import { formatDateInFromNow } from 'helpers/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import RunNewAnalysisModal from '../../modals/RunNewAnalysisModal';

const api = useApi();

const DattoSites = ({ created }) => {
  const DEFAULT_COOLDOWN_MINUTES = 60;

  const inputCustomer = useRef(null);

  const [sites, setSites] = useState({ content: [] });
  const [filters, setFilters] = useState({ page: 0, size: 20 });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerMap, setSelectedCustomerMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showAnalysisTool, setShowAnalysisTool] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [remainingTime, setRemainingTime] = useState(null);
  const [lastLog, setLastLog] = useState({
    syncDate: '',
    syncStatus: ''
  });
  const [filtersCustomer, setFiltersCustomer] = useState({}); // Estado de pesquisa por linha
  const [showMenuCustomer, setShowMenuCustomer] = useState({}); // Estado do menu por linha

  useEffect(() => {
    fetchSites();
  }, [filters, created]);

  useEffect(() => {
    initializeCustomers();
    fetchLastLog();
  }, []);

  useEffect(() => {
    initializeCooldown();
  }, []);

  useEffect(() => {
    if (remainingTime > 0) {
      const timer = setInterval(updateRemainingTime, 60000);
      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  const fetchSites = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await api.dattoSites.find(queryParams);
    setSites(response || { content: [] });
    initializeSelectedCustomerMap(response?.content || []);
    initializeFiltersCustomer(response?.content || []); // Inicializa o estado filtersCustomer
  };

  const fetchLastLog = async () => {
    const response = await api.dattoSites.logs.findLastLog();
    setLastLog(response.id ? response : {});
  };

  const initializeSelectedCustomerMap = sitesContent => {
    const initialSelected = sitesContent.reduce((acc, site) => {
      acc[site.id] = site.customer ? site.customer.id : '';
      return acc;
    }, {});
    setSelectedCustomerMap(initialSelected);
  };

  const initializeFiltersCustomer = sitesContent => {
    const initialFilters = sitesContent.reduce((acc, site) => {
      acc[site.id] = {
        name: site.customer ? site.customer.name : '' // Preenche com o nome do cliente mapeado
      };
      return acc;
    }, {});
    setFiltersCustomer(initialFilters);
  };

  const forceSync = async () => {
    setLoading(true);
    const request = await api.dattoSync.sites();

    if (request.ok) {
      onSyncSuccess();
    } else {
      const response = await request.json();
      handleSyncError(response.userMessage);
    }

    setLoading(false);
  };

  const onSyncSuccess = () => {
    fetchSites();
    setRemainingTime(DEFAULT_COOLDOWN_MINUTES);
    storeCooldown(DEFAULT_COOLDOWN_MINUTES);
    setSuccess('Sincronização feita com sucesso');
    setShowSuccess(true);
  };

  const handleSyncError = errorMessage => {
    setError(errorMessage);
    setShowError(true);
    calculateRemainingTimeFromError(errorMessage);
  };

  const calculateRemainingTimeFromError = errorMessage => {
    const match = errorMessage.match(/(\d+) minutos/);
    if (match) {
      const minutesLeft = parseInt(match[1], 10);
      setRemainingTime(minutesLeft);
      storeCooldown(minutesLeft);
    }
  };

  const storeCooldown = minutesLeft => {
    localStorage.setItem(
      'syncCooldown',
      JSON.stringify({
        minutesLeft,
        timestamp: Date.now()
      })
    );
  };

  const updateRemainingTime = () => {
    setRemainingTime(prevTime => {
      const newTime = prevTime - 1;
      if (newTime <= 0) {
        localStorage.removeItem('syncCooldown');
      }
      return newTime;
    });
  };

  const initializeCooldown = () => {
    const storedCooldown = localStorage.getItem('syncCooldown');
    if (storedCooldown) {
      const { minutesLeft, timestamp } = JSON.parse(storedCooldown);
      const elapsedTime = Math.floor((Date.now() - timestamp) / 60000);
      const updatedMinutesLeft = minutesLeft - elapsedTime;
      if (updatedMinutesLeft > 0) {
        setRemainingTime(updatedMinutesLeft);
      } else {
        localStorage.removeItem('syncCooldown');
      }
    }
  };

  const initializeCustomers = async () => {
    const filterCustomer = { page: 0, size: 1000 };
    const queryParams = new URLSearchParams(filterCustomer).toString();
    const response = await api.customers.list(queryParams);
    setCustomers(response.content || []);
  };

  const handleSelectChange = useCallback(
    (siteId, selectedCustomerId) => {
      setSelectedCustomerMap(prevState => ({
        ...prevState,
        [siteId]: selectedCustomerId
      }));

      // Atualiza o nome do cliente selecionado no campo de pesquisa
      const selectedCustomer = customers.find(
        customer => customer.id === selectedCustomerId
      );
      setFiltersCustomer(prevFilters => ({
        ...prevFilters,
        [siteId]: {
          ...prevFilters[siteId],
          name: selectedCustomer ? selectedCustomer.name : ''
        }
      }));

      // Fecha o menu após a seleção
      setShowMenuCustomer(prevState => ({
        ...prevState,
        [siteId]: false
      }));
    },
    [customers]
  );

  const handleCustomerSearchChange = (siteId, value) => {
    setFiltersCustomer(prevFilters => ({
      ...prevFilters,
      [siteId]: {
        ...prevFilters[siteId],
        name: value
      }
    }));

    // Se o campo de pesquisa estiver vazio, desmapeia o site
    if (value === '') {
      setSelectedCustomerMap(prevState => ({
        ...prevState,
        [siteId]: ''
      }));
    }
  };

  const handleMenuToggle = (siteId, isOpen) => {
    setShowMenuCustomer(prevState => ({
      ...prevState,
      [siteId]: isOpen
    }));
  };

  const handleSave = async () => {
    const { associations, disassociations } =
      getAssociationsAndDisassociations();

    const associateSuccess =
      associations.length > 0 && (await batchProcessAssociations(associations));
    const disassociateSuccess =
      disassociations.length > 0 &&
      (await batchProcessDisassociations(disassociations));

    if (associateSuccess || disassociateSuccess) {
      fetchSites();
    }
  };

  const getAssociationsAndDisassociations = () => {
    const associations = [];
    const disassociations = [];

    sites.content.forEach(site => {
      const originalCustomer = site.customer?.id || null;
      const selectedCustomer = selectedCustomerMap[site.id] || null;

      if (selectedCustomer && selectedCustomer !== originalCustomer) {
        associations.push({ siteId: site.id, customerId: selectedCustomer });
      }

      if (originalCustomer && !selectedCustomer) {
        disassociations.push(site.id);
      }
    });

    return { associations, disassociations };
  };

  const batchProcessAssociations = async associations => {
    const response = await api.dattoSites.batchAssociate(associations);
    if (response.ok) {
      setSuccess('Mapeamento alterado com sucesso');
      setShowSuccess(true);
      return true;
    } else {
      setError(response.userMessage);
      setShowError(true);
      return false;
    }
  };

  const batchProcessDisassociations = async disassociations => {
    const response = await api.dattoSites.batchDisassociate(disassociations);
    if (response.ok) {
      setSuccess('Mapeamento alterado com sucesso');
      setShowSuccess(true);
      return true;
    } else {
      setError(response.userMessage);
      setShowError(true);
      return false;
    }
  };

  return (
    <>
      {loading && <Loading />}
      <SuccessRequest
        message={success}
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
      />
      <FailedRequest
        message={error}
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
      />
      <RunNewAnalysisModal
        show={showAnalysisTool}
        close={() => setShowAnalysisTool(false)}
        refreshData={fetchSites}
      />

      <Row className="justify-content-between">
        <LastSyncStatus lastLog={lastLog} />
        <ActionButtons
          remainingTime={remainingTime}
          onForceSync={forceSync}
          onSave={handleSave}
          showAnalysisTool={() => setShowAnalysisTool(true)}
        />
      </Row>

      <hr />

      <Table responsive>
        <thead>
          <tr>
            <th className="text-900 fs-10">Site Datto RMM</th>
            <th className="w-75 text-900 fs-10">Cliente MSP Desk</th>
            <th>
              <Button variant="link" size="sm" onClick={() => fetchSites()}>
                <FontAwesomeIcon icon={faSyncAlt} />
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sites.content.map(site => (
            <tr key={site.id}>
              <td className="fs-10">{site.name}</td>
              <td>
                <Form.Control
                  type="text"
                  placeholder="Digite para pesquisar"
                  value={filtersCustomer[site.id]?.name || ''}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  onFocus={() => handleMenuToggle(site.id, true)}
                  onChange={e =>
                    handleCustomerSearchChange(site.id, e.target.value)
                  }
                />
                {showMenuCustomer[site.id] && (
                  <ul
                    className="position-sticky px-0 list-customer bg-100 shadow rounded z-3"
                    style={{
                      height: 'auto',
                      maxHeight: 350,
                      overflowY: 'auto'
                    }}
                  >
                    <li
                      style={{ listStyleType: 'none', cursor: 'pointer' }}
                      className="text-danger px-3 py-2 bg-primary-hover item-list-hover"
                      onClick={() => {
                        handleSelectChange(site.id, '');
                        setFiltersCustomer(prevFilters => ({
                          ...prevFilters,
                          [site.id]: { name: '' }
                        }));
                      }}
                    >
                      Não mapeado
                    </li>
                    {customers
                      .filter(customer =>
                        customer.name
                          ? customer.name
                              .toLowerCase()
                              .includes(
                                (
                                  filtersCustomer[site.id]?.name || ''
                                ).toLowerCase()
                              )
                          : customer.corporateName
                          ? customer.corporateName
                              .toLowerCase()
                              .includes(
                                (
                                  filtersCustomer[site.id]?.name || ''
                                ).toLowerCase()
                              )
                          : 'Cliente sem nome'
                      )
                      .map(item => (
                        <li
                          style={{ listStyleType: 'none', cursor: 'pointer' }}
                          className="px-3 py-2 bg-primary-hover item-list-hover"
                          key={item.id}
                          onClick={() => handleSelectChange(site.id, item.id)}
                        >
                          {item.name || item.corporateName}
                        </li>
                      ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination data={sites} filters={filters} setFilters={setFilters} />

      <Flex className="w-100" justifyContent="end">
        <Button variant="primary" size="sm" onClick={handleSave}>
          <FontAwesomeIcon icon={faSave} className="me-1" />
          Salvar
        </Button>
      </Flex>
    </>
  );
};

const LastSyncStatus = ({ lastLog }) =>
  lastLog.syncStatus ? (
    <Col className="col-auto">
      <span className="fs-10 fw-bold me-1">Última sincronização</span>
      <SubtleBadge bg={lastLog.syncStatus === 'SUCCESS' ? 'success' : 'danger'} pill>
        {lastLog.syncStatus === 'SUCCESS' ? 'Sucesso' : 'Erro'}
      </SubtleBadge>
      <span className="fs-10 ms-1">
        {formatDateInFromNow(lastLog.syncDate)}
      </span>
      <OverlayTrigger
        overlay={
          <Tooltip>
            A sincronização de Sites é feita todos os dias às 00:00, horário de
            Brasilia.
          </Tooltip>
        }
      >
        <span>
          <FontAwesomeIcon icon={faInfoCircle} size="sm" className="ms-2" />
        </span>
      </OverlayTrigger>
    </Col>
  ) : (
    <Col className="col-auto">
      <span>Não há logs de sincronização</span>
    </Col>
  );

const ActionButtons = ({
  remainingTime,
  onForceSync,
  onSave,
  showAnalysisTool
}) => (
  <Col className="col-auto" as={Flex}>
    <Button
      size="sm"
      className="me-2"
      onClick={onForceSync}
      disabled={remainingTime !== null && remainingTime > 0}
    >
      <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
      {remainingTime !== null && remainingTime > 0
        ? `Nova sinc. disponível em ${remainingTime} minutos`
        : 'Sincronizar Sites'}
    </Button>
    <Button
      variant="primary"
      size="sm"
      className="me-2"
      onClick={showAnalysisTool}
    >
      <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
      Executar uma nova análise
    </Button>
    <Button variant="primary" size="sm" onClick={onSave}>
      <FontAwesomeIcon icon={faSave} className="me-1" />
      Salvar
    </Button>
  </Col>
);

export default DattoSites;
